"use client";

import { useRef, useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, AlertTriangle, Package, ArrowRight, CheckCircle2, ChevronRight } from "lucide-react";
import { useStockAlerts } from "@/hooks/useStockAlerts";
import { mediaUrl, cn } from "@/lib/utils";
import type { Product } from "@/types";

const PREVIEW_LIMIT = 3;

export function NotificationDropdown() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { totalAlerts, outOfStockCount, lowStockCount, outOfStockProducts, lowStockProducts, isLoading } =
    useStockAlerts();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const badgeCount = totalAlerts > 99 ? "99+" : totalAlerts;

  return (
    <div ref={containerRef} className="relative">
      {/* Bell Button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border shadow-sm transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300",
          open
            ? "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200"
            : "border-slate-200 bg-white text-slate-500 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-800 dark:border-slate-800 dark:bg-slate-900/80 dark:text-slate-300 dark:shadow-black/20 dark:hover:border-sky-500/30 dark:hover:bg-sky-500/10 dark:hover:text-sky-200"
        )}
        aria-label="Bildirimler"
      >
        <Bell className="h-4 w-4" />
        {totalAlerts > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-0.5 text-[10px] font-bold leading-none text-white ring-2 ring-white dark:ring-slate-950">
            {badgeCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute right-0 top-full mt-2 w-96 rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 z-50 overflow-hidden dark:border-slate-800 dark:bg-slate-950 dark:shadow-black/40"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <Bell className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">Bildirimler</span>
              </div>
              {totalAlerts > 0 ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 dark:bg-red-500/10 px-2 py-0.5 text-[11px] font-semibold text-red-600 dark:text-red-400">
                  {totalAlerts} uyarı
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                  Temiz
                </span>
              )}
            </div>

            {/* Body */}
            <div className="max-h-[420px] overflow-y-auto">
              {isLoading ? (
                <div className="px-4 py-8 flex flex-col items-center gap-2 text-slate-400 dark:text-slate-500">
                  <div className="h-5 w-5 rounded-full border-2 border-slate-200 dark:border-slate-700 border-t-slate-400 dark:border-t-slate-400 animate-spin" />
                  <p className="text-xs">Yükleniyor…</p>
                </div>
              ) : totalAlerts === 0 ? (
                <div className="px-4 py-10 flex flex-col items-center gap-3 text-center">
                  <div className="h-10 w-10 rounded-full bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Her şey yolunda</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Stok uyarısı bulunmuyor.</p>
                  </div>
                </div>
              ) : (
                <div className="py-2">
                  {outOfStockCount > 0 && (
                    <AlertSection
                      title="Stokta Yok"
                      count={outOfStockCount}
                      products={outOfStockProducts}
                      tone="red"
                      stockLabel={(p) => `${p.stock} adet`}
                    />
                  )}
                  {lowStockCount > 0 && (
                    <AlertSection
                      title="Az Stokta"
                      count={lowStockCount}
                      products={lowStockProducts}
                      tone="amber"
                      stockLabel={(p) => `${p.stock} adet`}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            {totalAlerts > 0 && (
              <div className="border-t border-slate-100 dark:border-slate-800 px-4 py-2.5">
                <Link
                  href="/uyarilar"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-1.5 text-xs font-semibold text-sky-700 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 transition-colors py-1 rounded-lg hover:bg-sky-50 dark:hover:bg-sky-500/10"
                >
                  Tüm uyarıları görüntüle
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function AlertSection({
  title,
  count,
  products,
  tone,
  stockLabel,
}: {
  title: string;
  count: number;
  products: Product[];
  tone: "red" | "amber";
  stockLabel: (p: Product) => string;
}) {
  const palette = {
    red: {
      sectionBg: "bg-red-50/60 dark:bg-red-500/5",
      headerText: "text-red-700 dark:text-red-400",
      badge: "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400",
      icon: "text-red-400 dark:text-red-500",
      stockBadge: "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400",
      rowHover: "hover:bg-red-50 dark:hover:bg-red-500/5",
    },
    amber: {
      sectionBg: "bg-amber-50/60 dark:bg-amber-500/5",
      headerText: "text-amber-700 dark:text-amber-400",
      badge: "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400",
      icon: "text-amber-400 dark:text-amber-500",
      stockBadge: "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400",
      rowHover: "hover:bg-amber-50 dark:hover:bg-amber-500/5",
    },
  }[tone];

  const preview = products.slice(0, PREVIEW_LIMIT);
  const remainder = count - preview.length;

  return (
    <div className="mb-1">
      {/* Section Header */}
      <div className={cn("flex items-center gap-2 px-4 py-2", palette.sectionBg)}>
        <AlertTriangle className={cn("h-3 w-3 shrink-0", palette.icon)} />
        <span className={cn("text-[11px] font-bold uppercase tracking-wider", palette.headerText)}>
          {title}
        </span>
        <span className={cn("ml-auto inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-bold", palette.badge)}>
          {count}
        </span>
      </div>

      {/* Product Rows */}
      {preview.length > 0 ? (
        <div>
          {preview.map((product) => (
            <Link
              key={product.id}
              href={`/products/${product.id}`}
              className={cn(
                "flex items-center gap-3 px-4 py-2.5 transition-colors",
                palette.rowHover
              )}
            >
              {/* Thumbnail */}
              <div className="relative h-9 w-9 rounded-md bg-slate-100 dark:bg-slate-800 shrink-0 overflow-hidden border border-slate-200 dark:border-slate-700">
                {product.coverImageUrl ? (
                  <Image
                    src={mediaUrl(product.coverImageUrl)}
                    alt={product.name}
                    fill
                    sizes="36px"
                    className="object-cover"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center">
                    <Package className="h-3.5 w-3.5 text-slate-300 dark:text-slate-600" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-800 dark:text-slate-100 truncate">{product.name}</p>
                {product.categoryName && (
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">{product.categoryName}</p>
                )}
              </div>

              {/* Stock Badge */}
              <span className={cn("shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold", palette.stockBadge)}>
                {stockLabel(product)}
              </span>
            </Link>
          ))}

          {/* Remainder */}
          {remainder > 0 && (
            <Link
              href="/uyarilar"
              className="flex items-center gap-1.5 px-4 py-2 text-[11px] text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
            >
              <ChevronRight className="h-3 w-3" />
              {remainder} ürün daha
            </Link>
          )}
        </div>
      ) : (
        <div className="px-4 py-2.5">
          <p className="text-xs text-slate-500 dark:text-slate-400">{count} ürün etkilenmiş</p>
        </div>
      )}
    </div>
  );
}
